import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { FACE_MODEL_PATH, MEDIAPIPE_WASM_PATH } from '../config.js';
import { computePoseSignals, type PoseSignals } from './pose.js';

/** Oval bounds in NORMALIZED video-frame coordinates (§7 — same space as landmarks). */
export interface NormalizedOval {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Self-hosted MediaPipe FaceLandmarker wrapper — §18. Detection runs on the analysis
 * cadence; coordinates are normalized to the UNMIRRORED video frame (§7 — the selfie
 * preview mirror is CSS-only).
 */

export interface FaceObservation {
  faceCount: number;
  /** Normalized face bounding box in unmirrored video coordinates. */
  box: { x: number; y: number; w: number; h: number } | null;
  faceInOval: boolean;
  eyesVisible: boolean;
  /** RAW pose ratios — judged against the session baseline by the flow (pose.ts). */
  pose: PoseSignals | null;
}

export interface FaceEngineLike {
  load(): Promise<void>;
  readonly ready: boolean;
  /** True only for the E2E scripted engine — bypasses photo gates on the test stream. */
  readonly scripted: boolean;
  resetForNewStream(): void;
  detect(video: HTMLVideoElement, timestampMs: number, oval: NormalizedOval): FaceObservation;
  dispose(): void;
}

export class FaceEngine implements FaceEngineLike {
  readonly scripted = false;
  private landmarker: FaceLandmarker | null = null;
  private lastVideoTimeMs = -1;
  private lastObservation: FaceObservation = EMPTY_OBSERVATION;

  async load(): Promise<void> {
    const fileset = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
    const options = (delegate: 'GPU' | 'CPU') => ({
      baseOptions: { modelAssetPath: FACE_MODEL_PATH, delegate },
      runningMode: 'VIDEO' as const,
      numFaces: 2,
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: false,
    });
    try {
      this.landmarker = await FaceLandmarker.createFromOptions(fileset, options('GPU'));
    } catch {
      // Some mobile browsers reject the GPU delegate — CPU is slower but reliable.
      this.landmarker = await FaceLandmarker.createFromOptions(fileset, options('CPU'));
    }
  }

  get ready(): boolean {
    return this.landmarker !== null;
  }

  /**
   * Must be called at the start of every capture session: a fresh camera stream restarts
   * mediaTime near 0, and the monotonic-timestamp guard would otherwise freeze detection
   * on the previous session's high-water mark.
   */
  resetForNewStream(): void {
    this.lastVideoTimeMs = -1;
    this.lastObservation = EMPTY_OBSERVATION;
  }

  detect(video: HTMLVideoElement, timestampMs: number, oval: NormalizedOval): FaceObservation {
    if (!this.landmarker) return EMPTY_OBSERVATION;
    // MediaPipe VIDEO mode requires monotonically increasing timestamps.
    if (timestampMs <= this.lastVideoTimeMs) return this.lastObservation;
    this.lastVideoTimeMs = timestampMs;

    const result = this.landmarker.detectForVideo(video, timestampMs);
    const faces = result.faceLandmarks ?? [];
    if (faces.length === 0) {
      this.lastObservation = EMPTY_OBSERVATION;
      return this.lastObservation;
    }

    const landmarks = faces[0]!;
    let minX = 1;
    let minY = 1;
    let maxX = 0;
    let maxY = 0;
    for (const lm of landmarks) {
      if (lm.x < minX) minX = lm.x;
      if (lm.y < minY) minY = lm.y;
      if (lm.x > maxX) maxX = lm.x;
      if (lm.y > maxY) maxY = lm.y;
    }
    const box = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };

    // §18 — face inside the guidance oval. Generous bounds: the oval guides the user;
    // pixel-perfect containment must not be required from a hand-held phone.
    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    const ovalCx = oval.x + oval.w / 2;
    const ovalCy = oval.y + oval.h / 2;
    const ovalRx = Math.max(1e-6, oval.w / 2) * 1.25;
    const ovalRy = Math.max(1e-6, oval.h / 2) * 1.25;
    const inOval =
      ((cx - ovalCx) ** 2) / (ovalRx ** 2) + ((cy - ovalCy) ** 2) / (ovalRy ** 2) <= 1 &&
      box.w >= 0.3 * oval.w &&
      box.w <= 2.0 * oval.w;

    // "Both eyes reasonably visible" — blink blendshapes not near-closed.
    const shapes = result.faceBlendshapes?.[0]?.categories ?? [];
    const blinkL = shapes.find((c) => c.categoryName === 'eyeBlinkLeft')?.score ?? 0;
    const blinkR = shapes.find((c) => c.categoryName === 'eyeBlinkRight')?.score ?? 0;
    const eyesVisible = blinkL < 0.8 && blinkR < 0.8;

    this.lastObservation = {
      faceCount: faces.length,
      box,
      faceInOval: inOval,
      eyesVisible,
      pose: computePoseSignals(landmarks),
    };
    return this.lastObservation;
  }

  dispose(): void {
    this.landmarker?.close();
    this.landmarker = null;
  }
}

const EMPTY_OBSERVATION: FaceObservation = {
  faceCount: 0,
  box: null,
  faceInOval: false,
  eyesVisible: false,
  pose: null,
};

/**
 * E2E test seam — activated ONLY when sessionStorage 'kyc.e2eFace' is set (never in a
 * normal user flow; nothing enables it programmatically in production code). It replays
 * scripted observations from window.__kycFaceScript so the full pipeline (recording,
 * durations, uploads, backend §22 validation) is exercised in a real browser without a
 * live face. The backend still treats the session as WEB_LOW_ASSURANCE.
 */
export interface ScriptedFaceHooks {
  /** Called each detection tick; returns the observation to use. */
  next(elapsedMs: number, phaseKind: string): FaceObservation;
}

export class ScriptedFaceEngine implements FaceEngineLike {
  readonly scripted = true;
  ready = true;
  async load(): Promise<void> {
    /* nothing to load */
  }
  resetForNewStream(): void {
    /* stateless */
  }
  detect(_video: HTMLVideoElement, timestampMs: number, _oval: NormalizedOval): FaceObservation {
    const hooks = (window as unknown as { __kycFaceScript?: ScriptedFaceHooks }).__kycFaceScript;
    const phase = (window as unknown as { __kycLivenessPhase?: string }).__kycLivenessPhase ?? 'ready';
    if (!hooks) return EMPTY_OBSERVATION;
    return hooks.next(timestampMs, phase);
  }
  dispose(): void {
    /* nothing to dispose */
  }
}

export function isScriptedFaceMode(): boolean {
  // The E2E seam is ONLY ever active under a Vite dev server (E2E runs against `vite`).
  // `import.meta.env.DEV` is statically `false` in a production `vite build`, so this
  // whole branch is dead-code-eliminated from the shipped bundle — a real user cannot
  // switch it on via sessionStorage to bypass liveness.
  if (!import.meta.env.DEV) return false;
  try {
    return sessionStorage.getItem('kyc.e2eFace') === '1';
  } catch {
    return false;
  }
}
